import '../../stylesheets/booking_details.css';

import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';

import {
  cancelBookingById,
  getBookingDetails,
  sendBookingReceipt,
} from '../../actions/bookings';
import { DATE_FORMAT } from '../../constants/format';
import { format_booking_date, is_booking_canceled } from './bookingUtils';

const i18nPrefix = 'MaintainBookings';

const isCanceledBooking = (value) => {
  return value === true;
};

const BookingDetails = ({ bookingId, t, headerPaddings = {} }) => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadDetails = useCallback(() => {
    if (!bookingId) return;
    setLoading(true);
    getBookingDetails(
      bookingId,
      (response) => {
        setDetails(response);
        setLoading(false);
      },
      () => {
        setDetails(null);
        setLoading(false);
      },
    );
  }, [bookingId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  if (!bookingId) return null;

  const booking = details?.booking || details || {};
  const mooring = details?.mooring || booking.mooring || {};
  const boat = details?.boat || booking.boat || {};
  const canceled = is_booking_canceled(booking);
  const active = booking.active != null ? Boolean(booking.active) : false;
  const isCanceled = isCanceledBooking(booking.canceled ?? booking.cancelled);
  const isExpired =
    booking.to_date &&
    moment(booking.to_date).isBefore(moment(), 'day');
  const isCancelable =
    !canceled &&
    active &&
    booking.to_date &&
    moment(booking.to_date).isAfter(moment(), 'day');
  const canceled_on = format_booking_date(booking.canceled_on);
  const total_refund = booking.total_refund;

  const fromDate = booking.from_date
    ? moment(booking.from_date).format(DATE_FORMAT)
    : '';
  const toDate = booking.to_date
    ? moment(booking.to_date).format(DATE_FORMAT)
    : '';

  const yesText = t('global.yes', 'Yes');
  const noText = t('global.no', 'No');
  const formatValue = (value) => (value == null || value === '' ? '-' : value);
  const headerPaddingStyle = {
    paddingTop: headerPaddings.top || 0,
    paddingRight: headerPaddings.right || 0,
    paddingBottom: headerPaddings.bottom || 0,
    paddingLeft: headerPaddings.left || 0,
  };

  return (
    <div className='booking-details'>
      <div className='booking-details__header' style={headerPaddingStyle}>
        <div>
          <h1 className='booking-details__title'>
            {t(`${i18nPrefix}.details`, 'Details')}
          </h1>
          <div className='booking-details__subtitle'>
            {t(
              `${i18nPrefix}.your_booking_in`,
              'Your booking in {{harbour_name}}',
              {
                harbour_name: mooring.harbour_name || '',
              },
            )}
          </div>
        </div>
        <div className='booking-details__badges'>
          {canceled && (
            <span className='booking-details__badge booking-details__badge--warn'>
              {t(`${i18nPrefix}.canceled`, 'Canceled')}
            </span>
          )}
          <span
            className={`booking-details__badge ${
              booking.confirmed ? 'booking-details__badge--good' : ''
            }`}>
            {booking.confirmed
              ? t(`${i18nPrefix}.confirmed`, 'Confirmed')
              : t(`${i18nPrefix}.unconfirmed`, 'Unconfirmed')}
          </span>
          <span
            className={`booking-details__badge ${
              booking.payment_status
                ? 'booking-details__badge--good'
                : 'booking-details__badge--warn'
            }`}>
            {booking.payment_status
              ? t(`${i18nPrefix}.paid`, 'Paid')
              : t(`${i18nPrefix}.unpaid`, 'Unpaid')}
          </span>
        </div>
      </div>

      {loading && (
        <div className='booking-details__spinner'>
          <Spinner
            style={{ width: 18, height: 18 }}
            animation='border'
            variant='secondary'
            role='status'
          />
          {t('global.loading', 'Loading')}
        </div>
      )}

      <section className='booking-details__section'>
        <div className='booking-details__section-title'>
          {t(`${i18nPrefix}.bookingId`, 'Booking')}
        </div>
        <div className='booking-details__grid booking-details__grid--two'>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.bookingId`, 'Booking ID')}
            </div>
            <div className='booking-details__value'>
              {formatValue(booking.booking_id)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('Booking.reservationName', 'Reservation name')}
            </div>
            <div className='booking-details__value'>
              {formatValue(booking.reservation_name)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('MooringDetails.from', 'From')}
            </div>
            <div className='booking-details__value'>
              {formatValue(fromDate)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('MooringDetails.to', 'To')}
            </div>
            <div className='booking-details__value'>{formatValue(toDate)}</div>
          </div>
        </div>
      </section>

      <section className='booking-details__section'>
        <div className='booking-details__section-title'>
          {t(`${i18nPrefix}.guest`, 'Guest')}
        </div>
        <div className='booking-details__grid booking-details__grid--two'>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('MaintainTimesAndPrices.firstname', 'First name')}
            </div>
            <div className='booking-details__value'>
              {formatValue(details?.firstname || booking.firstname)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('MaintainTimesAndPrices.lastname', 'Last name')}
            </div>
            <div className='booking-details__value'>
              {formatValue(details?.lastname || booking.lastname)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('MaintainTimesAndPrices.email', 'Email')}
            </div>
            <div className='booking-details__value'>
              {formatValue(details?.email || booking.email)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('MaintainTimesAndPrices.mobile', 'Mobile')}
            </div>
            <div className='booking-details__value'>
              {formatValue(details?.mobilenumber || booking.mobilenumber)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.numberOfAdults`, 'Number of adults')}
            </div>
            <div className='booking-details__value'>
              {formatValue(booking.number_of_adults)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.numberOfChildren`, 'Number of children')}
            </div>
            <div className='booking-details__value'>
              {formatValue(booking.number_of_children)}
            </div>
          </div>
        </div>
        {booking.notes ? (
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.notes`, 'Notes')}
            </div>
            <div className='booking-details__text'>{booking.notes}</div>
          </div>
        ) : null}
      </section>

      <section className='booking-details__section'>
        <div className='booking-details__section-title'>
          {t(`${i18nPrefix}.payment`, 'Payment')}
        </div>
        <div className='booking-details__grid booking-details__grid--two'>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.pricePerNight`, 'Price per night')}
            </div>
            <div className='booking-details__value'>
              {formatValue(booking.daily_rate)} {booking.currency || ''}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.total_price`, 'Total price')}
            </div>
            <div className='booking-details__value'>
              {formatValue(booking.total_price)} {booking.currency || ''}
            </div>
          </div>
          {Number(booking.tax_price) !== 0 && (
            <>
              <div className='booking-details__item'>
                <div className='booking-details__label'>
                  {t(`${i18nPrefix}.booking_price`, 'Booking price')}
                </div>
                <div className='booking-details__value'>
                  {formatValue(booking.booking_price)} {booking.currency || ''}
                </div>
              </div>
              <div className='booking-details__item'>
                <div className='booking-details__label'>
                  {t(`${i18nPrefix}.tax_price`, 'Tax price')}
                </div>
                <div className='booking-details__value'>
                  {formatValue(booking.tax_price)} {booking.currency || ''}
                </div>
              </div>
            </>
          )}
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.marina_fee_included`, 'Marina fee included')}
            </div>
            <div className='booking-details__value'>
              {booking.harbor_fee_included ? yesText : noText}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.payment_method`, 'Payment method')}
            </div>
            <div className='booking-details__value'>
              {formatValue(booking.payment_method)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.internal_paid`, 'Internal paid')}
            </div>
            <div className='booking-details__value'>
              {booking.internal_paid ? yesText : noText}
            </div>
          </div>
        </div>
      </section>

      <section className='booking-details__section'>
        <div className='booking-details__section-title'>
          {t(`${i18nPrefix}.boat`, 'Boat')}
        </div>
        <div className='booking-details__grid booking-details__grid--two'>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('EditBoatModal.name', 'Name')}
            </div>
            <div className='booking-details__value'>
              {formatValue(boat.name)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('EditBoatModal.type', 'Type')}
            </div>
            <div className='booking-details__value'>
              {formatValue(boat.type_name || boat.type)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(
                `MaintainTimesAndPrices.boatRegistrationNumber`,
                'Registration number',
              )}
            </div>
            <div className='booking-details__value'>
              {formatValue(boat.registration_number)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`MaintainTimesAndPrices.boatDimensions`, 'Dimensions')}
            </div>
            <div className='booking-details__value'>
              {`${formatValue(boat.length)} / ${formatValue(
                boat.beam,
              )} / ${formatValue(boat.draft)} m`}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('EditBoatModal.weight', 'Weight')}
            </div>
            <div className='booking-details__value'>
              {formatValue(boat.weight)} kg
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t('EditBoatModal.country', 'Country')}
            </div>
            <div className='booking-details__value'>
              {formatValue(boat.country)}
            </div>
          </div>
        </div>
      </section>

      <section className='booking-details__section'>
        <div className='booking-details__section-title'>
          {t(`${i18nPrefix}.berthInfo`, 'Berth info')}
        </div>
        <div className='booking-details__grid'>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.marinaName`, 'Marina name')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.harbour_name)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.berthNo`, 'Berth no')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.berth_number)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.coordinates`, 'Coordinates')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.latitude)} / {formatValue(mooring.longitude)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.pierHeight`, 'Pier height')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.pier_height)} m
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.checkInTime`, 'Check-in time')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.check_in_time)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.checkOutTime`, 'Check-out time')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.check_out_time)}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.maxLength`, 'Max length')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.max_length)} m
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.maxBeam`, 'Max beam')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.max_beam)} m
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.maxDraught`, 'Max draught')}
            </div>
            <div className='booking-details__value'>
              {formatValue(mooring.max_draught)} m
            </div>
          </div>
          {mooring.max_weight ? (
            <div className='booking-details__item'>
              <div className='booking-details__label'>
                {t(`${i18nPrefix}.maxWeight`, 'Max weight')}
              </div>
              <div className='booking-details__value'>
                {formatValue(mooring.max_weight)} kg
              </div>
            </div>
          ) : null}
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.power`, 'Power')}
            </div>
            <div className='booking-details__value'>
              {mooring.power ? yesText : noText}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.water`, 'Water')}
            </div>
            <div className='booking-details__value'>
              {mooring.water ? yesText : noText}
            </div>
          </div>
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(`${i18nPrefix}.buoy`, 'Buoy')}
            </div>
            <div className='booking-details__value'>
              {mooring.buoy ? yesText : noText}
            </div>
          </div>
        </div>
        {mooring.additional_description ? (
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(
                `${i18nPrefix}.additionalDescription`,
                'Additional description',
              )}
            </div>
            <div className='booking-details__text'>
              {mooring.additional_description}
            </div>
          </div>
        ) : null}
        {active && mooring.confidential_description ? (
          <div className='booking-details__item'>
            <div className='booking-details__label'>
              {t(
                `${i18nPrefix}.confidentialDescription`,
                'Confidential description',
              )}
            </div>
            <div className='booking-details__text'>
              {mooring.confidential_description}
            </div>
          </div>
        ) : null}
      </section>

      <section className='booking-details__section'>
        {isCanceled && (
          <>
            <div className='booking-details__section-title'>
              {t(`${i18nPrefix}.cancellation`, 'Cancellation')}
            </div>
            <div className='booking-details__grid booking-details__grid--two'>
              <div className='booking-details__item'>
                <div className='booking-details__label'>
                  {t(`${i18nPrefix}.canceled`, 'Canceled')}
                </div>
                <div className='booking-details__value'>{yesText}</div>
              </div>
              <div className='booking-details__item'>
                <div className='booking-details__label'>
                  {t(`${i18nPrefix}.canceled_on`, 'Canceled on')}
                </div>
                <div className='booking-details__value'>
                  {formatValue(booking.canceled_on)}
                </div>
              </div>
              <div className='booking-details__item'>
                <div className='booking-details__label'>
                  {t(`${i18nPrefix}.cancel_comment`, 'Cancel comment')}
                </div>
                <div className='booking-details__value'>
                  {formatValue(booking.cancel_comment)}
                </div>
              </div>
            </div>
          </>
        )}
        <div className='booking-details__item'>
          <div className='booking-details__label'>
            {t(`${i18nPrefix}.actions`, 'Actions')}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {isExpired && (
              <Button
                type='button'
                variant='outline-primary'
                size='sm'
                disabled={actionLoading}
                onClick={() => {
                  const confirmed = window.confirm(
                    t(
                      `${i18nPrefix}.sendBookingReceiptConfirmation`,
                      'Send booking receipt?',
                    ),
                  );
                  if (!confirmed) return;
                  setActionLoading(true);
                  sendBookingReceipt(
                    booking.booking_id,
                    () => {
                      setActionLoading(false);
                    },
                    () => {
                      setActionLoading(false);
                    },
                  );
                }}>
                {t(`${i18nPrefix}.sendBookingReceipt`, 'Send receipt')}
              </Button>
            )}
            {isCancelable && (
              <Button
                type='button'
                variant='outline-danger'
                size='sm'
                disabled={actionLoading}
                onClick={() => {
                  const confirmed = window.confirm(
                    t(
                      `${i18nPrefix}.cancelBookingConfirmation`,
                      'Cancel this booking?',
                    ),
                  );
                  if (!confirmed) return;
                  setActionLoading(true);
                  cancelBookingById(
                    booking.booking_id,
                    () => {
                      setActionLoading(false);
                      loadDetails();
                    },
                    () => {
                      setActionLoading(false);
                    },
                  );
                }}>
                {t(`${i18nPrefix}.cancelBooking`, 'Cancel booking')}
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default withTranslation()(BookingDetails);
